import { useState, useEffect, useRef, Fragment } from "react"
import { ArrowLeft, Play, Image as ImageIcon, Film, MoreVertical, TrendingUp as PerformanceIcon, Eye, Trash2, X, LinkIcon } from "lucide-react";
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { SCREENS } from "../constant"
import { Dialog, Transition, Menu } from "@headlessui/react"
import "react-toastify";
import services from "../../../API"
import { CircularLoader } from "../../Loader/CircularLoader"
import Masonry from "react-masonry-css"

const imageBreakpointColumns = {
    default: 5,
    1024: 3,
    640: 2,
};

const videoBreakpointColumns = { ...imageBreakpointColumns, 640: 1 };

export function Creatives({ projectName, onBack, initialFilter = "video", onShowPerformance }) {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState(() => {
        return localStorage.getItem("creativesActiveFilter") || initialFilter;
    }); // "video" | "image"

    useEffect(() => {
        localStorage.setItem("creativesActiveFilter", activeFilter);
    }, [activeFilter]);
    const [videoAdsList, setVideoAdsList] = useState([]);
    const [imageAdsList, setImageAdsList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [inProcessAds, setInProcessAds] = useState([]);
    const inProcessAdsRef = useRef([]);
    const brandIdRef = useRef(localStorage.getItem("selectedBrandId") || "");
    const [playingVideoId, setPlayingVideoId] = useState(null);
    const [isVideoPaused, setIsVideoPaused] = useState(false);
    const [isSeeking, setIsSeeking] = useState({});
    const videoRefs = useRef({});
    const playingVideoIdRef = useRef(null);
    const [isMuted, ] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState({});
    const [videoBuffering, setVideoBuffering] = useState({});

    useEffect(() => {
        playingVideoIdRef.current = playingVideoId;
    }, [playingVideoId]);

    const handleMasterPlay = (videoId) => {
        setIsVideoPaused(false);
        const videoRefObj = videoRefs.current[videoId];
        if (videoRefObj) {
            if (videoRefObj.bg) videoRefObj.bg.play().catch(() => { });
            if (videoRefObj.overlay) videoRefObj.overlay.play().catch(() => { });
        }
    };

    const handleMasterPause = (videoId) => {
        setIsVideoPaused(true);
        const videoRefObj = videoRefs.current[videoId];
        if (videoRefObj) {
            if (videoRefObj.bg) videoRefObj.bg.pause();
            if (videoRefObj.overlay) videoRefObj.overlay.pause();
        }
    };

    const handleMasterSeek = (e, videoId) => {
        const time = e.target.currentTime;
        const videoRefObj = videoRefs.current[videoId];
        if (videoRefObj) {
            if (videoRefObj.bg) videoRefObj.bg.currentTime = time;
            if (videoRefObj.overlay) videoRefObj.overlay.currentTime = time;
        }
    };



    // Deletion state
    const [isOpensDelete, setIsOpenDelete] = useState(false);
    const [adToDelete, setAdToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedAds, setSelectedAds] = useState([]);

    // Text expansion state
    const [expandedAds, setExpandedAds] = useState([]);

    const toggleExpand = (adId) => {
        setExpandedAds((prev) =>
            prev.includes(adId) ? prev.filter((id) => id !== adId) : [...prev, adId]
        );
    };

    const toggleSelection = (id) => {
        setSelectedAds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const clearSelection = () => {
        setSelectedAds([]);
    };

    const handleVideoClick = (e, adId) => {
        if (e) e.stopPropagation();
        setPlayingVideoId(null);
        setIsVideoPaused(false);
        navigate("/app/videoeditor", { state: { videoAdId: adId, returnUrl: "/app/marketing", screen: SCREENS.CREATIVES } });
    };

    const handleImageAdClick = (e, adId) => {
        if (e) e.stopPropagation();
        navigate(`/app/ads/${adId}`, { state: { returnUrl: "/app/marketing", screen: SCREENS.CREATIVES } });
        window.scrollTo(0, 0);
    };

    const handleDeleteAdClick = (e, ad) => {
        e.stopPropagation();
        setAdToDelete(ad);
        setIsOpenDelete(true);
    };

    const confirmDeleteAd = async () => {
        const idsToDelete = adToDelete ? [adToDelete._id] : selectedAds;
        if (idsToDelete.length === 0) return;

        setIsDeleting(true);
        try {
            if (activeFilter === "video") {
                await services.deleteMultipleVideoAds(idsToDelete);
            } else {
                await services.deleteMultipleAds(idsToDelete);
            }

            // Optimistic refresh
            setVideoAdsList(prev => prev.filter(a => !idsToDelete.includes(a._id)));
            setImageAdsList(prev => prev.filter(a => !idsToDelete.includes(a._id)));

            setIsOpenDelete(false);
            setAdToDelete(null);
            setSelectedAds([]);
        } catch (error) {
            console.error(error);
        } finally {
            setIsDeleting(false);
        }
    };

    const checkGeneratingAdsStatus = async (brandId) => {
        const token = localStorage.getItem("token");
        try {
            const AIResponse = await axios.get(
                `${process.env.REACT_APP_API}ads/in-progress?brandId=${brandId}&type=AICMO`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const allAds = AIResponse.data.data.ads || [];
            // Filter to ensure only AI CMO ads are included, fixing cross-tab bleed
            return allAds.filter(ad => ad.cmoId || ad.aiCmoId || ad.type === "AICMO");
        } catch (error) {
            console.error(error);
            return [];
        }
    };

    const fetchAds = async () => {
        const token = localStorage.getItem("token");
        const bId = brandIdRef.current || localStorage.getItem("selectedBrandId");
        if (!bId) {
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.get(`${process.env.REACT_APP_API}ads/list?brandId=${bId}&type=AICMO`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const allAds = response?.data?.data?.ads || [];
            setVideoAdsList(allAds.filter(ad => ad.internalAdType === "video"));
            setImageAdsList(allAds.filter(ad => ad.internalAdType === "image"));
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        brandIdRef.current = localStorage.getItem("selectedBrandId");
        fetchAds();
        const check = async () => {
            const ads = await checkGeneratingAdsStatus(brandIdRef.current);
            setInProcessAds(ads);
        };
        check();
    }, []);

    useEffect(() => {
        setPlayingVideoId(null);
        setIsVideoPaused(false);
        setIsSeeking({});
    }, [activeFilter]);

    useEffect(() => {
        let interval;
        if (inProcessAds.length > 0) {
            inProcessAdsRef.current = inProcessAds;
            interval = setInterval(async () => {
                try {
                    const ads = await checkGeneratingAdsStatus(brandIdRef.current);

                    const currentAdIds = inProcessAdsRef.current.map(ad => ad._id);
                    const newAdIds = ads.map(ad => ad._id);
                    const hasCompletedAds = currentAdIds.some(id => !newAdIds.includes(id));

                    if (hasCompletedAds || ads.length === 0) {
                        await fetchAds();
                    }

                    inProcessAdsRef.current = ads;
                    setInProcessAds(ads);

                    if (ads.length === 0) {
                        clearInterval(interval);
                    }
                } catch (err) {
                    console.error("Error checking generating ads:", err);
                }
            }, 15000);
        }
        return () => { if (interval) clearInterval(interval); };
    }, [inProcessAds]);

    const VideoSkeletonCard = () => (
        <div className="h-fit rounded-xl overflow-hidden border bg-white border-gray-200 dark:bg-white/5 dark:border-white/10">
            <div className="p-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-orange-500/20 animate-pulse"></div>
                <div className="flex-1 space-y-2">
                    <div className="h-3 rounded w-1/2 animate-pulse bg-gray-200 dark:bg-white/10"></div>
                </div>
            </div>
            <div className="relative aspect-[9/16] animate-pulse bg-gray-100 dark:bg-white/5">
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin"></div>
                    <p className="text-xs font-medium animate-pulse text-orange-500 dark:text-orange-400">Generating Video...</p>
                </div>
            </div>
            <div className="p-3 space-y-2">
                <div className="h-3 rounded w-3/4 animate-pulse bg-gray-200 dark:bg-white/10"></div>
            </div>
        </div>
    );

    const ImageSkeletonCard = () => (
        <div className="h-fit rounded-xl overflow-hidden border bg-white border-gray-200 dark:bg-white/5 dark:border-white/10">
            <div className="p-3 flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-orange-500/20 animate-pulse"></div>
                <div className="flex-1 space-y-2">
                    <div className="h-3 rounded w-1/2 animate-pulse bg-gray-200 dark:bg-white/10"></div>
                </div>
            </div>
            <div className="relative aspect-square animate-pulse bg-gray-100 dark:bg-white/5">
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin"></div>
                    <p className="text-xs font-medium animate-pulse text-orange-500 dark:text-orange-400">Generating Image Ad...</p>
                </div>
            </div>
            <div className="p-3 space-y-2">
                <div className="h-3 rounded w-3/4 animate-pulse bg-gray-200 dark:bg-white/10"></div>
            </div>
        </div>
    );

    const isVideoActive = activeFilter === "video";
    const currentList = isVideoActive ? videoAdsList : imageAdsList;

    const inProcessVideoCount = inProcessAds.filter(ad => ad.internalAdType === "video").length;
    const inProcessImageCount = inProcessAds.filter(ad => ad.internalAdType === "image").length;
    const currentInProcessCount = isVideoActive ? inProcessVideoCount : inProcessImageCount;

    return (
        <div className="flex-1 min-w-0 overflow-y-auto py-6 px-4 sm:px-6 lg:px-10">
            <div className="w-full min-w-0 space-y-6">
                {/* Header */}
                <div className="space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-1">
                            {projectName && (
                                <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                                    {projectName}
                                </p>
                            )}
                            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                                Creatives
                            </h1>
                            <p className="text-sm text-gray-700 dark:text-gray-400">
                                Your generated video and image ads
                            </p>
                        </div>
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center gap-2 self-start px-4 py-2 rounded-lg border text-sm transition-colors border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to Concepts
                            </button>
                        )}
                    </div>

                    {/* Selection Header */}
                    {selectedAds.length > 0 && (
                        <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="text-blue-800 dark:text-blue-200 font-medium">
                                    {selectedAds.length} {selectedAds.length === 1 ? 'item' : 'items'} selected
                                </span>
                                <button
                                    onClick={clearSelection}
                                    className="text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/30 px-3 py-1 rounded text-sm flex items-center gap-1 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                    Clear selection
                                </button>
                            </div>
                            <button
                                onClick={() => setIsOpenDelete(true)}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete Batch
                            </button>
                        </div>
                    )}

                    {/* Filter tabs — Video / Image */}
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setActiveFilter("video")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeFilter === "video"
                                ? "bg-orange-500 text-white shadow-sm"
                                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white dark:border-white/10"
                                }`}
                        >
                            <Film className="w-4 h-4" />
                            Video Ads
                            {(videoAdsList.length > 0 || inProcessVideoCount > 0) && (
                                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${activeFilter === "video"
                                    ? "bg-white/20 text-white"
                                    : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300"
                                    }`}>
                                    {videoAdsList.length + inProcessVideoCount}
                                </span>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveFilter("image")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeFilter === "image"
                                ? "bg-orange-500 text-white shadow-sm"
                                : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-white dark:border-white/10"
                                }`}
                        >
                            <ImageIcon className="w-4 h-4" />
                            Image Ads
                            {(imageAdsList.length > 0 || inProcessImageCount > 0) && (
                                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${activeFilter === "image"
                                    ? "bg-white/20 text-white"
                                    : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300"
                                    }`}>
                                    {imageAdsList.length + inProcessImageCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Ads grid — filtered by tab */}
                <Masonry
                    breakpointCols={isVideoActive ? videoBreakpointColumns : imageBreakpointColumns}
                    className="marketing-creatives-grid flex -ml-4 w-auto"
                    columnClassName="marketing-creatives-grid__column pl-4 bg-clip-padding"
                >
                    {/* Skeleton cards for in-progress */}
                    {isVideoActive && inProcessAds
                        .filter(ad => ad.internalAdType === "video")
                        .map((_, idx) => <VideoSkeletonCard key={`vskeleton-${idx}`} />)
                    }
                    {!isVideoActive && inProcessAds
                        .filter(ad => ad.internalAdType === "image")
                        .map((_, idx) => <ImageSkeletonCard key={`iskeleton-${idx}`} />)
                    }

                    {/* Initial loading */}
                    {isLoading && currentList.length === 0 && currentInProcessCount === 0 &&
                        Array.from({ length: 4 }).map((_, idx) =>
                            isVideoActive
                                ? <VideoSkeletonCard key={`vload-${idx}`} />
                                : <ImageSkeletonCard key={`iload-${idx}`} />
                        )}

                    {/* Video cards */}
                    {isVideoActive && videoAdsList.map((video) => {
                        const videoUrl = video.video?.url || video.videoUrl;
                        const hasVideo = videoUrl && videoUrl.trim() !== "";
                        const isSelected = selectedAds.includes(video._id);

                        return (
                            <div
                                key={video._id}
                                className={`border mb-4 rounded-3xl shadow-sm duration-300 transition-all overflow-hidden cursor-pointer relative group
                                hover:bg-white/5 hover:shadow-xl hover:shadow-black/50 hover:scale-[1.02]
                                ${isSelected
                                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                                        : "border-gray-200 bg-white dark:border-gray-700 dark:bg-[#111111]"
                                    }`}
                                onClick={(e) => {
                                    if (selectedAds.length > 0) {
                                        toggleSelection(video._id);
                                    } else {
                                        handleVideoClick(e, video._id);
                                    }
                                }}
                            >
                                {/* Selection Checkbox */}
                                <div
                                    className={`absolute top-2 left-2 z-10 selection-checkbox ${isSelected || "group-hover:opacity-100 opacity-0"} transition-opacity`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSelection(video._id);
                                    }}
                                >
                                    <div
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
                                            ? "bg-blue-600 border-blue-600"
                                            : "bg-white dark:bg-[#0A0A0A] border-gray-300 dark:border-gray-600 hover:border-blue-400"
                                            }`}
                                    >
                                        {isSelected && (
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {/* Brand Header */}
                                <div className="flex min-w-0 items-center justify-between gap-1 p-2 pb-3 sm:p-4 sm:pb-3">
                                    <div className="flex min-w-0 items-center gap-1.5 sm:gap-3">
                                        <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                                            <span className="text-white font-bold text-sm">
                                                {video?.brandId?.brandName?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-gray-900 dark:text-[#fff] text-xs sm:text-sm truncate">
                                                {video?.brandId?.brandName}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {video?.characterId?.name &&
                                                    `Avatar: ${video.characterId.name}`}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                            onClick={(e) => handleVideoClick(e, video._id)}
                                            title="View Details"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <Menu as="div" className="relative">
                                            <Menu.Button
                                                onClick={(e) => e.stopPropagation()}
                                                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                            >
                                                <MoreVertical className="w-5 h-5" />
                                            </Menu.Button>
                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-100"
                                                enterFrom="transform opacity-0 scale-95"
                                                enterTo="transform opacity-100 scale-100"
                                                leave="transition ease-in duration-75"
                                                leaveFrom="transform opacity-100 scale-100"
                                                leaveTo="transform opacity-0 scale-95"
                                            >
                                                <Menu.Items className="absolute right-0 mt-2 w-32 origin-top-right rounded-lg bg-white dark:bg-[#1a1a1a] shadow-lg ring-1 ring-black/5 focus:outline-none z-50 overflow-hidden border border-gray-100 dark:border-white/5">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={(e) => handleDeleteAdClick(e, video)}
                                                                className={`${active ? "bg-red-50 dark:bg-red-500/10 text-red-600" : "text-gray-700 dark:text-gray-300"} group flex w-full items-center px-3 py-2 text-sm transition-colors`}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" />
                                                                Delete
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                </Menu.Items>
                                            </Transition>
                                        </Menu>
                                    </div>
                                </div>

                                <hr className="dark:border-gray-700" />

                                {/* Video Player / Poster */}
                                <div className="relative flex h-full w-full items-center justify-center overflow-hidden min-h-[200px]">
                                    {video.adType === 'talking_head' ? (() => {
                                        const hasBg = Boolean(video.backgroundMedia?.url);
                                        const hasOverlay = Boolean(video.video?.url);
                                        const bgReady = !hasBg || Boolean(videoLoaded[`${video._id}-bg`]);
                                        const overlayReady = !hasOverlay || Boolean(videoLoaded[`${video._id}-overlay`]);
                                        const allLayersReady = bgReady && overlayReady;
                                        return (
                                            <div
                                                className="relative w-full aspect-[9/16] bg-black overflow-hidden group/talking-head cursor-pointer"
                                                onClick={(e) => {
                                                    if (!allLayersReady) return;
                                                    e.stopPropagation();
                                                    if (playingVideoId !== video._id) {
                                                        setPlayingVideoId(video._id);
                                                        setIsVideoPaused(false);
                                                    }
                                                }}
                                            >
                                                {/* Background Layer */}
                                                {hasBg && (
                                                    <div
                                                        className="absolute w-full h-full"
                                                        style={{
                                                            left: `${(video.backgroundMedia.appearance?.x || 0.5) * 100}%`,
                                                            top: `${(video.backgroundMedia.appearance?.y || 0.5) * 100}%`,
                                                            transform: `translate(-50%, -50%) scale(${video.backgroundMedia.appearance?.scale || 1}) rotate(${video.backgroundMedia.appearance?.rotation || 0}deg)`,
                                                            opacity: video.backgroundMedia.appearance?.opacity ?? 1,
                                                        }}
                                                    >
                                                        {video.backgroundMedia.type === 'video' ? (
                                                            <video
                                                                src={video.backgroundMedia.url}
                                                                className="w-full h-full object-cover"
                                                                autoPlay={playingVideoId === video._id && !isVideoPaused}
                                                                muted={true}
                                                                loop
                                                                playsInline
                                                                preload="metadata"
                                                                onLoadedData={() => setVideoLoaded(prev => ({ ...prev, [`${video._id}-bg`]: true }))}
                                                                onError={() => setVideoLoaded(prev => ({ ...prev, [`${video._id}-bg`]: true }))}
                                                                ref={(el) => {
                                                                    if (el) {
                                                                        if (typeof videoRefs.current[video._id] !== "object" || videoRefs.current[video._id] === null) {
                                                                            videoRefs.current[video._id] = {};
                                                                        }
                                                                        videoRefs.current[video._id].bg = el;
                                                                        if (playingVideoId === video._id) {
                                                                            if (isVideoPaused) el.pause();
                                                                            else el.play().catch(() => { });
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={video.backgroundMedia.url}
                                                                className="w-full h-full object-cover"
                                                                alt="Background"
                                                                onLoad={() => setVideoLoaded(prev => ({ ...prev, [`${video._id}-bg`]: true }))}
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                                {/* Talking Head Layer */}
                                                {hasOverlay && (
                                                    <div
                                                        className="absolute w-full h-full"
                                                        style={{
                                                            left: `${(video.video.appearance?.x || 0.5) * 100}%`,
                                                            top: `${(video.video.appearance?.y || 0.5) * 100}%`,
                                                            transform: `translate(-50%, -50%) scale(${video.video.appearance?.scale || 1}) rotate(${video.video.appearance?.rotation || 0}deg)`,
                                                            opacity: video.video.appearance?.opacity ?? 1,
                                                            zIndex: 10,
                                                        }}
                                                    >
                                                        <video
                                                            src={video.video.url}
                                                            className="w-full h-full object-contain"
                                                            autoPlay={playingVideoId === video._id && !isVideoPaused}
                                                            muted={true}
                                                            loop
                                                            playsInline
                                                            preload="metadata"
                                                            onLoadedData={() => setVideoLoaded(prev => ({ ...prev, [`${video._id}-overlay`]: true }))}
                                                            onError={() => setVideoLoaded(prev => ({ ...prev, [`${video._id}-overlay`]: true }))}
                                                            ref={(el) => {
                                                                if (el) {
                                                                    if (typeof videoRefs.current[video._id] !== "object" || videoRefs.current[video._id] === null) {
                                                                        videoRefs.current[video._id] = {};
                                                                    }
                                                                    videoRefs.current[video._id].overlay = el;
                                                                    if (playingVideoId === video._id) {
                                                                        if (isVideoPaused) el.pause();
                                                                        else el.play().catch(() => { });
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                {/* Loading Overlay */}
                                                {(!allLayersReady || videoBuffering[video._id]) && (
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800/50 z-30 pointer-events-none">
                                                        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                                            {videoBuffering[video._id] ? "Buffering..." : "Loading..."}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Play/Pause Overlay */}
                                                {allLayersReady && !videoBuffering[video._id] && (playingVideoId !== video._id || (isVideoPaused && !isSeeking[video._id])) && (
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover/talking-head:bg-black/40 transition-colors pointer-events-none z-20">
                                                        <div
                                                            className="w-12 h-12 rounded-full bg-[#F4F4F5] flex items-center justify-center text-[#101828] shadow-lg transform group-hover/talking-head:scale-110 transition-transform cursor-pointer pointer-events-auto"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (playingVideoId && videoRefs.current[playingVideoId]) {
                                                                    const oldVideo = videoRefs.current[playingVideoId];
                                                                    if (oldVideo.master) oldVideo.master.pause();
                                                                    else if (typeof oldVideo.pause === 'function') oldVideo.pause();
                                                                }
                                                                if (playingVideoId !== video._id) {
                                                                    setPlayingVideoId(video._id);
                                                                    setIsVideoPaused(false);
                                                                } else {
                                                                    setIsVideoPaused(false);
                                                                }
                                                                const el = videoRefs.current[video._id]?.master;
                                                                if (el && typeof el.play === 'function') {
                                                                    el.play().catch(() => { });
                                                                }
                                                            }}
                                                        >
                                                            <Play className="w-6 h-6 fill-white ml-0.5" />
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Master video (controls synced layers) */}
                                                <video
                                                    src={video.video?.url}
                                                    controls={playingVideoId === video._id}
                                                    muted={isMuted}
                                                    preload="metadata"
                                                    playsInline
                                                    className={`absolute inset-0 w-full h-full z-40 bg-transparent ${playingVideoId === video._id ? 'pointer-events-auto' : 'pointer-events-none'}`}
                                                    style={{ objectPosition: "-9999px -9999px", opacity: playingVideoId === video._id ? 1 : 0 }}
                                                    onPlay={() => handleMasterPlay(video._id)}
                                                    onPause={() => handleMasterPause(video._id)}
                                                    onWaiting={() => setVideoBuffering(prev => ({ ...prev, [video._id]: true }))}
                                                    onPlaying={() => {
                                                        setVideoBuffering(prev => ({ ...prev, [video._id]: false }));
                                                        setIsVideoPaused(false);
                                                    }}
                                                    onSeeking={(e) => {
                                                        setIsSeeking(prev => ({ ...prev, [video._id]: true }));
                                                        handleMasterSeek(e, video._id);
                                                    }}
                                                    onSeeked={(e) => {
                                                        handleMasterSeek(e, video._id);
                                                        setIsSeeking(prev => ({ ...prev, [video._id]: false }));
                                                    }}
                                                    onTimeUpdate={(e) => handleMasterSeek(e, video._id)}
                                                    ref={(el) => {
                                                        if (el) {
                                                            if (typeof videoRefs.current[video._id] !== "object" || videoRefs.current[video._id] === null) {
                                                                videoRefs.current[video._id] = {};
                                                            }
                                                            videoRefs.current[video._id].master = el;
                                                        }
                                                    }}
                                                />
                                            </div>
                                        );
                                    })() : hasVideo ? (
                                        <div className="relative w-full aspect-[9/16] bg-black group/play overflow-hidden">
                                            {/* Always mount video for background buffering */}
                                            <video
                                                ref={(el) => (videoRefs.current[video._id] = el)}
                                                src={videoUrl}
                                                controls={playingVideoId === video._id}
                                                muted={isMuted}
                                                playsInline
                                                preload="metadata"
                                                onClick={(e) => e.stopPropagation()}
                                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 z-10 ${playingVideoId === video._id ? 'opacity-100' : 'opacity-0'}`}
                                                onPlay={() => {
                                                    if (playingVideoId === video._id) setIsVideoPaused(false);
                                                }}
                                                onPause={() => {
                                                    if (playingVideoId === video._id) setIsVideoPaused(true);
                                                }}
                                                onSeeking={() => setIsSeeking(prev => ({ ...prev, [video._id]: true }))}
                                                onSeeked={() => setIsSeeking(prev => ({ ...prev, [video._id]: false }))}
                                            />

                                            {/* Poster/GIF Overlay */}
                                            <img
                                                src={video.gifUrl || video.video?.thumbnail}
                                                alt={video.videoTitle}
                                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 z-0 pointer-events-none ${playingVideoId === video._id ? 'opacity-0' : 'opacity-100'}`}
                                            />

                                            {/* Play/Pause Overlay when Playing and Paused */}
                                            {playingVideoId === video._id && isVideoPaused && !isSeeking[video._id] && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover/play:bg-black/40 transition-colors pointer-events-none z-20">
                                                    <div
                                                        className="w-12 h-12 rounded-full bg-[#F4F4F5] flex items-center justify-center text-[#101828] shadow-lg transform hover:scale-110 transition-transform cursor-pointer pointer-events-auto"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setIsVideoPaused(false);
                                                            const el = videoRefs.current[video._id];
                                                            if (el && typeof el.play === 'function') {
                                                                el.play().catch(() => { });
                                                            }
                                                        }}
                                                    >
                                                        <Play className="w-6 h-6 fill-white ml-0.5" />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Initial Play Button Overlay when NOT Playing */}
                                            {playingVideoId !== video._id && (
                                                <div
                                                    className="absolute inset-0 flex items-center justify-center group-hover/play:bg-black/40 transition-colors cursor-pointer z-20"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (playingVideoId && videoRefs.current[playingVideoId]) {
                                                            const oldVideo = videoRefs.current[playingVideoId];
                                                            if (oldVideo.master) oldVideo.master.pause();
                                                            else if (typeof oldVideo.pause === 'function') oldVideo.pause();
                                                        }
                                                        setIsVideoPaused(false);
                                                        setIsSeeking(false);
                                                        setPlayingVideoId(video._id);
                                                        const el = videoRefs.current[video._id];
                                                        if (el && typeof el.play === 'function') {
                                                            el.play().catch(() => { });
                                                        }
                                                    }}
                                                >
                                                    <div className="w-14 h-14 rounded-full bg-[#F4F4F5] flex items-center justify-center shadow-lg transition-transform duration-200 hover:scale-110">
                                                        <Play className="h-7 w-7 text-[#101828] ml-1" fill="white" strokeWidth={1.75} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="w-full aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                            <span className="text-gray-400">No video available</span>
                                        </div>
                                    )}

                                    {/* Link Icon */}
                                    <div
                                        className="absolute rounded-lg flex items-center justify-center top-2 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-7 w-7 dark:bg-[#111111]/90 dark:hover:bg-[#111111] dark:text-white border border-white/10 bg-white/90 hover:bg-white text-gray-900 border-gray-200"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <a
                                            href={video?.brandId?.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <LinkIcon className="h-4 w-4" />
                                        </a>
                                    </div>

                                    {/* Variation Counter */}
                                    {video?.variations?.length > 0 && (
                                        <div
                                            className={`absolute bottom-2 left-2 bg-orange-500 text-white text-[10px] px-2 py-1 rounded-md z-50 flex items-center font-medium transition-opacity duration-300 ${playingVideoId === video._id ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {(video.variations.length || 0) + 1} variations
                                        </div>
                                    )}

                                    {/* Video Orientation Badge */}
                                    {video.videoOrientation && (
                                        <div className={`absolute bottom-2 right-2 z-50 bg-white dark:bg-[#0A0A0A] rounded-md px-2 py-1 text-[10px] font-medium text-gray-500 dark:text-[#fff] flex items-center border border-gray-200 dark:border-gray-700 transition-opacity duration-300 ${playingVideoId === video._id ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                                            {video.videoOrientation === "portrait" ? (
                                                <>
                                                    <svg width="6" height="12" viewBox="0 0 6 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
                                                        <rect x="0.5" y="0.5" width="5" height="11" rx="1" stroke="currentColor" fill="none" />
                                                    </svg>
                                                    9:16
                                                </>
                                            ) : (
                                                <>
                                                    <svg width="12" height="8" viewBox="0 0 16 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1">
                                                        <rect x="0.5" y="0.5" width="15" height="8" rx="1" stroke="currentColor" fill="none" />
                                                    </svg>
                                                    16:9
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="border-t border-gray-200 dark:border-gray-700"></div>
                                <div className="p-3">
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                                            {video?.brandId?.url
                                                ? new URL(video?.brandId?.url).hostname
                                                : "Video Ad"}
                                        </div>
                                        <h3 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">
                                            {video.videoTitle || "Untitled Video"}
                                        </h3>
                                        {video.type && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {video.type}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {/* Image cards */}
                    {!isVideoActive && imageAdsList.map((ad) => {
                        const hasImage = ad.imageUrl && ad.imageUrl.trim() !== "";
                        const coverImageFound = ad?.imageVariations?.find(
                            (variation) => variation?.url === ad?.imageUrl
                        );
                        const isSelected = selectedAds.includes(ad._id);
                        const isTextExpanded = expandedAds.includes(ad._id);
                        const isLongText = ad?.adCopyText?.length > 100;
                        const displayText = isTextExpanded
                            ? ad?.adCopyText
                            : ad?.adCopyText?.substring(0, 100)?.trimEnd() + "...";

                        return (
                            <div
                                key={ad._id}
                                onClick={(e) => {
                                    if (selectedAds.length > 0) {
                                        toggleSelection(ad._id);
                                    } else {
                                        handleImageAdClick(e, ad._id);
                                    }
                                }}
                                className={`marketing-creative-image-card border mb-4 rounded-3xl shadow-sm duration-300 transition-all overflow-hidden cursor-pointer relative group
                                hover:bg-white/5 hover:shadow-xl hover:shadow-black/50 hover:scale-[1.02]
                                ${isSelected
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-[#111111]'
                                    }`}
                            >
                                <div
                                    className={`absolute top-2 left-2 z-10 selection-checkbox ${isSelected || "group-hover:opacity-100 opacity-0"} transition-opacity`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSelection(ad._id);
                                    }}
                                >
                                    <div
                                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
                                            ? "bg-blue-600 border-blue-600"
                                            : "bg-white dark:bg-[#0A0A0A] border-gray-300 dark:border-gray-600 hover:border-blue-400"
                                            }`}
                                    >
                                        {isSelected && (
                                            <svg
                                                className="w-3 h-3 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <div className="flex min-w-0 items-center justify-between gap-1 p-2 pb-3 sm:p-4 sm:pb-3">
                                    <div className="flex min-w-0 items-center gap-1.5 sm:gap-3">
                                        <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                                            <span className="text-white font-bold text-sm">
                                                {ad?.brandId?.brandName?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-semibold text-gray-900 dark:text-[#fff] text-xs sm:text-sm truncate">
                                                {ad?.brandId?.brandName}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                                        <Menu as="div" className="relative">
                                            <Menu.Button
                                                className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                                title="More Options"
                                            >
                                                <MoreVertical className="w-5 h-5" />
                                            </Menu.Button>
                                            <Transition
                                                as={Fragment}
                                                enter="transition ease-out duration-100"
                                                enterFrom="transform opacity-0 scale-95"
                                                enterTo="transform opacity-100 scale-100"
                                                leave="transition ease-in duration-75"
                                                leaveFrom="transform opacity-100 scale-100"
                                                leaveTo="transform opacity-0 scale-95"
                                            >
                                                <Menu.Items className="absolute right-0 mt-2 w-32 origin-top-right rounded-lg bg-white dark:bg-[#1a1a1a] shadow-lg ring-1 ring-black/5 focus:outline-none z-50 overflow-hidden border border-gray-100 dark:border-white/5">
                                                    <Menu.Item>
                                                        {({ active }) => (
                                                            <button
                                                                onClick={(e) => handleDeleteAdClick(e, ad)}
                                                                className={`${active ? 'bg-red-50 dark:bg-red-500/10 text-red-600' : 'text-gray-700 dark:text-gray-300'
                                                                    } group flex w-full items-center px-3 py-2 text-sm transition-colors`}
                                                            >
                                                                <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                                                                Delete
                                                            </button>
                                                        )}
                                                    </Menu.Item>
                                                </Menu.Items>
                                            </Transition>
                                        </Menu>
                                    </div>
                                </div>
                                <hr className="dark:border-gray-700" />
                                <div className="px-2 sm:px-4 pb-3 mt-2">
                                    <div className="text-gray-900 dark:text-[#fff] text-sm leading-relaxed">
                                        <span className="whitespace-pre-line text-black dark:text-gray-300">
                                            {displayText}
                                        </span>
                                        {isLongText && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleExpand(ad._id);
                                                }}
                                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ml-2 font-medium z-50"
                                            >
                                                {isTextExpanded ? "See less" : "See more"}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
                                    {hasImage ? (
                                        <img
                                            src={ad.imageUrl}
                                            alt="Ad"
                                            className={`${(coverImageFound?.size === "1024x1024" || coverImageFound?.size === "1:1")
                                                ? "max-h-full max-w-full aspect-square object-cover"
                                                : (coverImageFound?.size === "1024x1536" || coverImageFound?.size === "9:16")
                                                    ? "w-full max-h-[450px] aspect-[9/16] object-cover"
                                                    : coverImageFound?.size === "4:5"
                                                        ? "w-full max-h-[400px] aspect-[4/5] object-cover"
                                                        : (coverImageFound?.size === "1536x1024" || coverImageFound?.size === "16:9")
                                                            ? "w-full aspect-video object-cover"
                                                            : "max-h-full max-w-full object-cover"
                                                } border border-gray-200 dark:border-gray-700 ring-1 ring-gray-200 dark:ring-gray-700`}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <div className="text-center">
                                                <ImageIcon className="w-10 h-10 mx-auto mb-2 text-gray-400 dark:text-gray-600" />
                                                <span className="text-gray-400 dark:text-gray-500 text-xs">No image</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Overlays */}
                                    <div
                                        className="absolute rounded-lg flex items-center justify-center top-2 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-7 w-7 dark:bg-[#111111]/90 dark:hover:bg-[#111111] dark:text-white border border-white/10 bg-white/90 hover:bg-white text-gray-900 border-gray-200"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onShowPerformance?.(ad._id);
                                        }}
                                        title="Performance"
                                    >
                                        <PerformanceIcon className="w-4 h-4" />
                                    </div>

                                    {/* Variation Counter */}
                                    {ad?.imageVariations?.length > 1 && (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            className="absolute bottom-2 left-2 bg-orange-500 text-white text-[10px] px-2 py-1 rounded-md z-50 flex items-center font-medium"
                                        >
                                            {ad.imageVariations.length} variations
                                        </div>
                                    )}

                                    <div
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute bottom-2 right-2 bg-white dark:bg-[#0A0A0A] rounded-md px-2 py-1 text-[10px] font-medium text-gray-500 dark:text-[#fff] flex items-center border border-gray-200 dark:border-gray-700"
                                    >
                                        {(coverImageFound?.size === "1024x1024" || coverImageFound?.size === "1:1") ? (
                                            <svg
                                                width="10"
                                                height="10"
                                                viewBox="0 0 10 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="mr-1"
                                            >
                                                <rect x="0.5" y="0.5" width="9" height="9" rx="1" stroke="currentColor" fill="none" />
                                            </svg>
                                        ) : (coverImageFound?.size === "1024x1536" || coverImageFound?.size === "9:16") ? (
                                            <svg
                                                width="6"
                                                height="12"
                                                viewBox="0 0 6 12"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="mr-1"
                                            >
                                                <rect x="0.5" y="0.5" width="5" height="11" rx="1" stroke="currentColor" fill="none" />
                                            </svg>
                                        ) : coverImageFound?.size === "4:5" ? (
                                            <svg
                                                width="8"
                                                height="10"
                                                viewBox="0 0 8 10"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="mr-1"
                                            >
                                                <rect x="0.5" y="0.5" width="7" height="9" rx="1" stroke="currentColor" fill="none" />
                                            </svg>
                                        ) : (
                                            <svg
                                                width="12"
                                                height="8"
                                                viewBox="0 0 16 9"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="mr-1"
                                            >
                                                <rect x="0.5" y="0.5" width="15" height="8" rx="1" stroke="currentColor" fill="none" />
                                            </svg>
                                        )}

                                        {(coverImageFound?.size === "1024x1024" || coverImageFound?.size === "1:1")
                                            ? "1:1"
                                            : (coverImageFound?.size === "1024x1536" || coverImageFound?.size === "9:16")
                                                ? "9:16"
                                                : coverImageFound?.size === "4:5"
                                                    ? "4:5"
                                                    : "16:9"}
                                    </div>
                                </div>
                                <div className="border-t border-gray-200 dark:border-gray-700"></div>
                                <div className="p-3">
                                    <div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                                            {ad?.brandId?.url ? new URL(ad.brandId.url).hostname : ""}
                                        </div>
                                        <h3 className="font-semibold text-gray-900 dark:text-[#fff] text-sm">
                                            {ad.headline}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                                            {ad.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Masonry>

                {/* Empty state for current filter */}
                {!isLoading && currentInProcessCount === 0 && currentList.length === 0 && (
                    <div className="text-center py-16 border border-dashed rounded-xl border-gray-300 text-gray-400 dark:border-white/10 dark:text-gray-500">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-100 dark:bg-white/5">
                                {isVideoActive ? <Play className="w-6 h-6" /> : <ImageIcon className="w-6 h-6" />}
                            </div>
                            <p className="text-sm font-medium">
                                No {isVideoActive ? "video" : "image"} ads yet
                            </p>
                            <p className="text-xs">Go back to Concepts to generate your first {isVideoActive ? "video" : "image"} ad</p>
                            {onBack && (
                                <button
                                    onClick={onBack}
                                    className="mt-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors"
                                >
                                    Back to Concepts
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <Transition appear show={isOpensDelete} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-50"
                    onClose={() => setIsOpenDelete(false)}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-[#0A0A0A] p-6 text-left align-middle shadow-xl transition-all text-center">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900 dark:text-[#fff]"
                                    >
                                        Delete {adToDelete ? 'Creative' : `${selectedAds.length} Creatives`}
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {adToDelete
                                                ? 'Are you sure? This will delete the original and all its variations.'
                                                : `Are you sure you want to delete these ${selectedAds.length} items? This action cannot be undone.`
                                            }
                                        </p>
                                    </div>

                                    <div className="mt-6 flex justify-center gap-4">
                                        <button
                                            type="button"
                                            className="px-8 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full text-sm font-medium transition-colors flex items-center justify-center min-w-[80px] disabled:opacity-50"
                                            onClick={confirmDeleteAd}
                                            disabled={isDeleting}
                                        >
                                            {isDeleting ? <CircularLoader size={18} color="white" /> : "Yes"}
                                        </button>
                                        <button
                                            type="button"
                                            className="px-8 py-2 bg-gray-900 hover:bg-black text-white dark:bg-white/10 dark:hover:bg-white/20 rounded-full text-sm font-medium transition-colors"
                                            onClick={() => setIsOpenDelete(false)}
                                        >
                                            No
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    )
}
